import React, { useState, useEffect } from "react";

/* =========================================================
   Système de notifications (toasts) + confirmation modale
   Léger, sans dépendance, thème épuré noir & blanc MBeauty
   ========================================================= */

// ---------- TOASTS ----------
let toastListeners = [];
let toastList = [];
let toastId = 0;

function emitToasts() {
  toastListeners.forEach((l) => l([...toastList]));
}

function pushToast(message, type = "info", duration = 4200) {
  if (!message) return;
  const id = ++toastId;
  toastList = [...toastList, { id, message: String(message), type }];
  emitToasts();
  if (duration) {
    setTimeout(() => dismissToast(id), duration);
  }
  return id;
}

function dismissToast(id) {
  toastList = toastList.filter((t) => t.id !== id);
  emitToasts();
}

export const toast = {
  success: (m) => pushToast(m, "success"),
  error: (m) => pushToast(m, "error"),
  info: (m) => pushToast(m, "info"),
  show: (m, type) => pushToast(m, type),
};

// Détection auto du type à partir du contenu (pour remplacer window.alert)
const SUCCESS_HINTS = [
  "succès", "réussi", "merci", "confirmé", "créé", "créée", "ajouté",
  "supprimé", "approuvé", "rejeté", "enregistré", "envoyé", "réinitialisé",
  "installée", "installé",
];
const ERROR_HINTS = [
  "erreur", "incorrect", "invalide", "échec", "impossible", "expiré",
  "obligatoire", "ne correspond", "au moins", "déjà", "n'est plus",
  "doit contenir", "aucun", "annulé",
];

export function smartToast(message) {
  const lower = String(message).toLowerCase();
  if (ERROR_HINTS.some((h) => lower.includes(h)) && !lower.includes("avec succès")) {
    return pushToast(message, "error");
  }
  if (SUCCESS_HINTS.some((h) => lower.includes(h))) {
    return pushToast(message, "success");
  }
  return pushToast(message, "info");
}

const ICONS = {
  success: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
  ),
  error: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6M9 9l6 6" /></svg>
  ),
  info: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
  ),
};

export function Toaster() {
  const [items, setItems] = useState(toastList);
  useEffect(() => {
    toastListeners.push(setItems);
    return () => {
      toastListeners = toastListeners.filter((l) => l !== setItems);
    };
  }, []);

  return (
    <div className="mb-toaster" data-testid="toast-container" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className={`mb-toast mb-toast-${t.type}`} data-testid={`toast-${t.type}`}>
          <span className="mb-toast-icon">{ICONS[t.type] || ICONS.info}</span>
          <span className="mb-toast-msg">{t.message}</span>
          <button className="mb-toast-close" onClick={() => dismissToast(t.id)} aria-label="Fermer">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>
      ))}
    </div>
  );
}

// ---------- CONFIRMATION MODALE ----------
let confirmListener = null;

export function confirmAction(options = {}) {
  return new Promise((resolve) => {
    if (!confirmListener) {
      // Fallback si le composant n'est pas monté
      resolve(window.confirm(options.message || "Confirmer ?"));
      return;
    }
    confirmListener({
      title: options.title || "Confirmation",
      message: options.message || "Êtes-vous sûr ?",
      confirmText: options.confirmText || "Confirmer",
      cancelText: options.cancelText || "Annuler",
      danger: options.danger || false,
      resolve,
    });
  });
}

export function ConfirmDialog() {
  const [state, setState] = useState(null);

  useEffect(() => {
    confirmListener = (opts) => setState(opts);
    return () => {
      confirmListener = null;
    };
  }, []);

  if (!state) return null;

  const close = (result) => {
    state.resolve(result);
    setState(null);
  };

  return (
    <div className="mb-confirm-overlay" onClick={() => close(false)} data-testid="confirm-overlay">
      <div className="mb-confirm-box" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={`mb-confirm-icon ${state.danger ? "danger" : ""}`}>
          {state.danger ? (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><path d="M12 9v4M12 17h.01" /></svg>
          ) : (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
          )}
        </div>
        <h3 className="mb-confirm-title">{state.title}</h3>
        <p className="mb-confirm-message">{state.message}</p>
        <div className="mb-confirm-actions">
          <button className="mb-btn-ghost" onClick={() => close(false)} data-testid="confirm-cancel-btn">
            {state.cancelText}
          </button>
          <button
            className={state.danger ? "mb-btn-danger" : "mb-btn-solid"}
            onClick={() => close(true)}
            data-testid="confirm-accept-btn"
          >
            {state.confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
